import { useLoadableState } from '@carvana/merch-react-hooks';
import { useContext, useEffect } from 'react';
import { PrimaryButton } from '@/common/Button';
import TrackWrapper from '@/common/TrackWrapper';
import { fireAction } from '@/home/HeroCardsReturnUser/Cards/actionNames';
import DefaultCardImage from '@/home/HeroCardsReturnUser/Cards/DefaultCardImage';
import LoadingCard, {
  CardType
} from '@/home/HeroCardsReturnUser/Cards/LoadingCard';
import useHasPurchaseInProgress from '@/home/hooks/useHasPurchaseInProgress';
import formatEvent from '@/home/utils/formatEvent';
import Sections from '~/constants/home/sections';
import { useIsNative } from '~/context/common/DeviceContext';
import {
  useGetExpirationDate,
  useHasExpiredSoftpull,
  useHasValidSoftPull,
  useIsExpiredSoon,
  useIsFinanceLoaded,
  useMaxMonthly
} from '~/context/home/FinanceContext';
import ModalContext from '~/context/home/ModalContext';
import { useNextBestActionIsLoaded } from '~/context/home/NextBestActionContext';
import { useNativeOpenSoftPullOnClick } from '~/native/home/hooks';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import {
  defaultFinanceCardData,
  type FinanceCardData,
  FinanceCardStatus,
  getFinanceCardData
} from './constants';
import StatusIndicator from '../StatusIndicator';
import {
  CardContainer,
  FinanceGridWrapper,
  Heading,
  Paragraph
} from '../styles';

function FinanceCard() {
  const {
    data: cardData,
    setData: setCardData,
    isLoaded: isCardDataLoaded,
    setLoading: setCardDataLoading
  } = useLoadableState<FinanceCardData>(defaultFinanceCardData);
  const isNextActionLoaded = useNextBestActionIsLoaded();
  const hasPurchaseInProgress = useHasPurchaseInProgress();
  const handleNativeOpenSoftPullOnClick = useNativeOpenSoftPullOnClick();
  const maxMonthly = useMaxMonthly();
  const expiredDate = useGetExpirationDate();
  const isFinanceLoaded = useIsFinanceLoaded();
  const hasValidSoftPull = useHasValidSoftPull();
  const hasSoftPullExpiringSoon = useIsExpiredSoon();
  const hasExpiredSoftPull = useHasExpiredSoftpull();
  const hasNoSoftPullStatus = !hasValidSoftPull;
  const hasActiveSoftPull = hasValidSoftPull || hasSoftPullExpiringSoon;
  const { setSoftPullModalOpen } = useContext(ModalContext);
  const isNative = useIsNative();

  useEffect(() => {
    if (!isFinanceLoaded) return;
    setCardDataLoading();
    const newCardData = getFinanceCardData({
      expiredDate,
      hasExpiredSoftPull,
      hasValidSoftPull,
      hasSoftPullExpiringSoon,
      hasNoSoftPullStatus
    });
    setCardData(newCardData);
  }, [
    expiredDate,
    hasExpiredSoftPull,
    hasNoSoftPullStatus,
    hasSoftPullExpiringSoon,
    hasValidSoftPull,
    isFinanceLoaded,
    setCardData,
    setCardDataLoading
  ]);

  const hideCTA = hasPurchaseInProgress && hasValidSoftPull;

  return isCardDataLoaded && isNextActionLoaded ? (
    <TrackWrapper
      event={formatEvent('Viewed - Home feed card', {
        description: 'Finance card',
        status: cardData.status,
        cta: cardData.buttonTitle
      })}
      trackOnce
    >
      <CardContainer
        className="gap-16"
        isDefault={cardData.status === FinanceCardStatus.Default}
      >
        <FinanceGridWrapper
          defaultLayout={cardData.status === FinanceCardStatus.Default}
        >
          <div className="flex justify-between overflow-hidden">
            <Heading className="truncate" title={cardData.title}>
              {cardData.title}
            </Heading>

            {(hasActiveSoftPull || hasExpiredSoftPull) && (
              <StatusIndicator
                statusColor={cardData.statusColor}
                text={cardData.statusText}
              />
            )}
          </div>

          <DefaultCardImage
            defaultLayout={cardData.status === FinanceCardStatus.Default}
            url={'/images/graph-financing.png'}
            altText="graph"
          />

          <div>
            <Paragraph>{cardData.description}</Paragraph>
            {hasActiveSoftPull && (
              <h4 className="text-blue-6 tl-header-m">
                ${maxMonthly.toLocaleString()}
                <span className="text-blue-6 tl-header-s">/mo</span>
              </h4>
            )}
          </div>
        </FinanceGridWrapper>

        <a
          data-analytics-id="Home feed card"
          data-analytics-attributes={JSON.stringify({
            description: `Finance card`,
            status: cardData.status,
            cta: cardData.buttonTitle
          })}
          data-qa="Finance promo tile cta"
          data-native-included={!cardData.openSoftPullModal}
          data-native-payload={cardData.nativePayload}
          onClick={event => {
            fireAction(CardType.Finance);
            if (cardData.openSoftPullModal) {
              event.preventDefault();
              handleNativeOpenSoftPullOnClick(event);
              if (!isNative) {
                setSoftPullModalOpen(true);
              }
            }
          }}
          href={cardData.buttonUrl}
          className={hideCTA ? 'hidden' : ''}
        >
          <PrimaryButton className="h-40 min-h-[40px] w-full">
            {cardData.buttonTitle}
          </PrimaryButton>
        </a>
      </CardContainer>
    </TrackWrapper>
  ) : (
    <LoadingCard card={CardType.Finance} />
  );
}

export default withErrorBoundary(
  FinanceCard,
  undefined,
  undefined,
  Sections.FinanceCard
);
